import * as THREE from 'three';
import { beveledBox, box, mergeParts, part, steppedGable, type Part } from '@/art/geometry';
import { emissive, vertexColored } from '@/art/materials';
import type { PaletteName } from '@/art/palette';
import { CITY } from '@/world/grid';
import { Rng, seedFrom } from '@/util/rng';

type Facing = 'north' | 'south' | 'east' | 'west';

export const CANAL_HOUSE = {
  widthModules: 1,
  width: CITY.facadeModule,
  depth: 8,
  floors: 4,
  floorHeight: CITY.floorHeight,
  gableHeight: 2.4,
  windowWidth: 0.78,
  windowHeight: 1.12,
  doorWidth: 0.86,
  doorHeight: 2.05,
  shutterWidth: 0.22,
  litChance: 0.52,
  triBudget: 1650,
} as const;

export const TENEMENT = {
  widthModules: 3,
  width: CITY.facadeModule * 3,
  depth: 10,
  floors: 4,
  floorHeight: CITY.floorHeight,
  parapetHeight: 0.55,
  windowWidth: 0.9,
  windowHeight: 1.05,
  entryWidth: 1.25,
  entryHeight: 2.25,
  fireEscapeWidth: 2.9,
  litChance: 0.38,
  triBudget: 2250,
} as const;

export const CHURCH = {
  widthModules: 2,
  width: CITY.facadeModule * 2,
  depth: 13,
  /** Verdiepingen van het schip; toren en spits schalen mee in vaste verhouding. */
  floors: 3,
  naveHeight: 3 * CITY.floorHeight,
  towerHeight: 16.8,
  spireHeight: 3.6,
  triBudget: 1550,
} as const;

/**
 * Maten horen bij de plaatsing, niet bij de builder.
 *
 * Deze drie builders negeerden `width`, `depth` en `floors` volledig en bouwden altijd hun
 * defaultmaat. Omdat `LevelLoader` de hele placement als variabele doorgaf, klaagde
 * TypeScript daar niet over: elke tenement werd 12 m breed terwijl de leveldata 10 zei, en
 * de botsingsblokken volgden de data. Vandaar dat je door gevels heen liep en dat rijen in
 * elkaar stonden. De maten zijn nu verplicht onderdeel van het contract.
 */
export interface UrbanBuildingOptions {
  readonly width?: number;
  readonly depth?: number;
  readonly floors?: number;
  readonly facing?: Facing;
  readonly wall?: PaletteName;
  readonly seed?: string;
}

/** Raamkolommen op het gevelritme van het stadsgrid, uitgezet over de werkelijke breedte. */
function facadeColumns(width: number): number[] {
  const count = Math.max(2, Math.round(width / CITY.facadeModule));
  const spacing = width / (count + 1);
  return Array.from({ length: count }, (_, index) => -width / 2 + spacing * (index + 1));
}

export function buildCanalHouse(options: UrbanBuildingOptions = {}): THREE.Group {
  const facing = options.facing ?? 'south';
  const wall = options.wall ?? 'nightBrick';
  const rng = new Rng(seedFrom(options.seed ?? 'canal-house'));
  const base = CANAL_HOUSE;
  const spec = {
    ...base,
    width: options.width ?? base.width,
    depth: options.depth ?? base.depth,
    floors: options.floors ?? base.floors,
  };
  const wallHeight = spec.floors * spec.floorHeight;
  const front = spec.depth / 2;

  const group = new THREE.Group();
  group.name = 'NA_Building_CanalHouse';
  group.rotation.y = facingRotation(facing);

  const solid: Part[] = [
    part(beveledBox(spec.width, wallHeight, spec.depth, 0.035).translate(0, wallHeight / 2, 0), wall),
    part(box(spec.width + 0.14, 0.45, spec.depth + 0.12).translate(0, 0.225, 0), 'nightStone'),
    part(box(spec.width + 0.26, 0.16, 0.22).translate(0, wallHeight - 0.1, front + 0.11), 'nightStone'),
    part(box(spec.width + 0.42, 0.16, 0.28).translate(0, wallHeight + 0.05, front + 0.18), 'darkSteel'),
    part(box(spec.width + 0.24, 0.12, 0.22).translate(0, wallHeight - 0.1, -front - 0.11), 'darkSteel'),
    part(box(0.13, wallHeight - 0.7, 0.13).translate(-spec.width / 2 - 0.11, wallHeight / 2 + 0.1, front + 0.12), 'darkSteel'),
    part(box(0.11, wallHeight - 0.4, 0.12).translate(spec.width / 2 + 0.11, wallHeight / 2 + 0.1, front + 0.12), 'darkSteel'),
    part(
      steppedGable(spec.width + 0.18, spec.gableHeight, 0.5, 4).translate(
        0,
        wallHeight,
        front + 0.06,
      ),
      wall,
    ),
    part(box(spec.doorWidth, spec.doorHeight, 0.12).translate(0, spec.doorHeight / 2, front + 0.07), 'charcoal'),
    part(box(spec.doorWidth + 0.24, 0.12, 0.16).translate(0, spec.doorHeight + 0.08, front + 0.11), 'nightStone'),
    part(box(spec.doorWidth + 0.36, 0.12, 0.52).translate(0, 0.2, front + 0.24), 'nightStone'),
    part(box(1.18, 0.16, 0.7).translate(0, 0.08, front + 0.5), 'pavement'),
    part(box(0.18, 1.55, 0.16).translate(-0.53, 0.82, front + 0.16), 'darkSteel'),
    part(box(0.18, 1.55, 0.16).translate(0.53, 0.82, front + 0.16), 'darkSteel'),
    part(
      box(spec.width + 0.08, 0.24, 0.34).translate(0, wallHeight + spec.gableHeight + 0.12, front + 0.02),
      'nightStone',
    ),
    part(box(0.36, 0.82, 0.36).translate(-spec.width * 0.28, wallHeight + 0.41, -spec.depth * 0.225), 'darkSteel'),
    part(box(0.28, 0.62, 0.28).translate(spec.width * 0.3, wallHeight + 0.31, spec.depth * 0.275), 'nightStone'),
  ];
  const lights: Part[] = [];

  // Een grachtenpand is één gevelmodule breed: twee ramen naast elkaar, iets wijder op de
  // begane grond waar de deur ertussen zit.
  for (let floor = 0; floor < spec.floors; floor++) {
    const y = floor * spec.floorHeight + spec.floorHeight * 0.62;
    const spread = spec.width * (floor === 0 ? 0.2375 : 0.205);
    const xs = [-spread, spread];
    for (const x of xs) {
      if (floor === 0 && Math.abs(x) < spec.doorWidth) continue;
      const window = box(spec.windowWidth, spec.windowHeight, 0.08).translate(x, y, front + 0.08);
      if (rng.chance(spec.litChance)) lights.push(part(window, 'windowWarm'));
      else solid.push(part(window, 'charcoal'));

      solid.push(
        part(box(spec.windowWidth + 0.2, 0.1, 0.12).translate(x, y + spec.windowHeight / 2 + 0.08, front + 0.11), 'nightStone'),
        part(box(spec.windowWidth + 0.24, 0.12, 0.14).translate(x, y - spec.windowHeight / 2 - 0.08, front + 0.12), 'nightStone'),
        part(box(0.08, spec.windowHeight + 0.16, 0.12).translate(x - spec.windowWidth / 2 - 0.04, y, front + 0.11), 'nightStone'),
        part(box(0.08, spec.windowHeight + 0.16, 0.12).translate(x + spec.windowWidth / 2 + 0.04, y, front + 0.11), 'nightStone'),
        part(box(0.06, spec.windowHeight + 0.02, 0.11).translate(x, y, front + 0.12), 'darkSteel'),
        part(box(spec.windowWidth + 0.1, 0.06, 0.11).translate(x, y, front + 0.12), 'darkSteel'),
        part(
          box(spec.shutterWidth, spec.windowHeight + 0.08, 0.1).translate(
            x - spec.windowWidth / 2 - spec.shutterWidth / 2 - 0.05,
            y,
            front + 0.09,
          ),
          'forestGreen',
        ),
        part(
          box(spec.shutterWidth, spec.windowHeight + 0.08, 0.1).translate(
            x + spec.windowWidth / 2 + spec.shutterWidth / 2 + 0.05,
            y,
            front + 0.09,
          ),
          'forestGreen',
        ),
      );
    }
  }

  setFootprint(group, spec.width, spec.depth, wallHeight);
  addMeshes(group, solid, lights);
  return group;
}

export function buildTenement(options: UrbanBuildingOptions = {}): THREE.Group {
  const facing = options.facing ?? 'south';
  const wall = options.wall ?? 'oxideMaroon';
  const rng = new Rng(seedFrom(options.seed ?? 'tenement'));
  const base = TENEMENT;
  const spec = {
    ...base,
    width: options.width ?? base.width,
    depth: options.depth ?? base.depth,
    floors: options.floors ?? base.floors,
    fireEscapeWidth: (options.width ?? base.width) * (base.fireEscapeWidth / base.width),
  };
  const wallHeight = spec.floors * spec.floorHeight;
  const front = spec.depth / 2;
  const halfWidth = spec.width / 2;

  const group = new THREE.Group();
  group.name = 'NA_Building_Tenement';
  group.rotation.y = facingRotation(facing);

  const solid: Part[] = [
    part(beveledBox(spec.width, wallHeight, spec.depth, 0.04).translate(0, wallHeight / 2, 0), wall),
    part(box(spec.width + 0.16, 0.5, spec.depth + 0.12).translate(0, 0.25, 0), 'nightStone'),
    part(box(spec.width + 0.26, 0.16, 0.24).translate(0, wallHeight - 0.12, front + 0.12), 'nightStone'),
    part(box(spec.width + 0.4, 0.16, 0.28).translate(0, wallHeight + 0.12, front + 0.18), 'darkSteel'),
    part(box(spec.width + 0.26, 0.14, 0.22).translate(0, wallHeight + 0.1, -front - 0.12), 'darkSteel'),
    part(
      box(spec.width + 0.2, spec.parapetHeight, spec.depth + 0.16).translate(
        0,
        wallHeight + spec.parapetHeight / 2,
        0,
      ),
      'nightStone',
    ),
    part(
      box(spec.entryWidth, spec.entryHeight, 0.16).translate(0, spec.entryHeight / 2, front + 0.07),
      'charcoal',
    ),
    part(box(spec.entryWidth + 0.64, 0.18, 0.72).translate(0, 0.09, front + 0.42), 'pavement'),
    part(box(spec.entryWidth + 0.4, 0.16, 0.25).translate(0, spec.entryHeight + 0.14, front + 0.16), 'nightStone'),
  ];
  const lights: Part[] = [];

  const columns = facadeColumns(spec.width);
  for (let floor = 0; floor < spec.floors; floor++) {
    const y = floor * spec.floorHeight + spec.floorHeight * 0.62;
    for (const x of columns) {
      if (floor === 0 && Math.abs(x) < spec.entryWidth) continue;
      const window = box(spec.windowWidth, spec.windowHeight, 0.08).translate(x, y, front + 0.08);
      if (rng.chance(spec.litChance)) lights.push(part(window, 'windowWarm'));
      else solid.push(part(window, 'charcoal'));
      solid.push(
        part(box(spec.windowWidth + 0.34, 0.12, 0.14).translate(x, y - 0.66, front + 0.11), 'nightStone'),
        part(box(spec.windowWidth + 0.28, 0.1, 0.12).translate(x, y + 0.62, front + 0.11), 'nightStone'),
        part(box(0.08, spec.windowHeight + 0.14, 0.12).translate(x - spec.windowWidth / 2 - 0.05, y, front + 0.11), 'darkSteel'),
        part(box(0.08, spec.windowHeight + 0.14, 0.12).translate(x + spec.windowWidth / 2 + 0.05, y, front + 0.11), 'darkSteel'),
        part(box(0.06, spec.windowHeight, 0.12).translate(x, y, front + 0.12), 'darkSteel'),
        part(box(spec.windowWidth + 0.12, 0.06, 0.12).translate(x, y, front + 0.12), 'darkSteel'),
      );
    }
  }

  // De brandtrap hangt op de rechterhelft van de gevel. Alles hieronder staat uitgedrukt als
  // fractie van de halve breedte, zodat een smaller pand geen trap naast zijn gevel krijgt.
  const escapeX = halfWidth * 0.358;
  const escapeHalf = spec.fireEscapeWidth / 2;
  for (let floor = 1; floor < spec.floors; floor++) {
    const y = floor * spec.floorHeight + 0.4;
    solid.push(part(box(spec.fireEscapeWidth, 0.12, 0.52).translate(escapeX, y, front + 0.36), 'darkSteel'));
    solid.push(part(box(spec.fireEscapeWidth, 0.09, 0.1).translate(escapeX, y + 0.42, front + 0.58), 'darkSteel'));
    solid.push(part(box(spec.fireEscapeWidth, 0.07, 0.1).translate(escapeX, y + 0.68, front + 0.58), 'darkSteel'));
    solid.push(part(box(0.1, 0.54, 0.1).translate(escapeX - escapeHalf + 0.05, y + 0.24, front + 0.58), 'darkSteel'));
    solid.push(part(box(0.1, 0.54, 0.1).translate(escapeX + escapeHalf - 0.05, y + 0.24, front + 0.58), 'darkSteel'));
    solid.push(part(box(0.08, 0.4, 0.08).translate(escapeX - escapeHalf * 0.46, y + 0.2, front + 0.58), 'darkSteel'));
    solid.push(part(box(0.08, 0.4, 0.08).translate(escapeX + escapeHalf * 0.46, y + 0.2, front + 0.58), 'darkSteel'));
  }

  solid.push(part(box(0.16, (spec.floors - 1) * spec.floorHeight, 0.12).translate(halfWidth - 0.22, wallHeight / 2, front + 0.58), 'darkSteel'));
  solid.push(part(box(0.18, wallHeight - 0.6, 0.16).translate(-halfWidth - 0.1, wallHeight / 2, front + 0.12), 'darkSteel'));
  solid.push(part(box(0.56, 0.48, 0.56).translate(-halfWidth * 0.683, wallHeight + 0.24, -spec.depth * 0.26), 'nightStone'));
  solid.push(part(box(0.34, 0.7, 0.34).translate(halfWidth * 0.6, wallHeight + 0.35, spec.depth * 0.24), 'darkSteel'));
  for (const fraction of [-0.733, -0.2, 0.367, 0.733]) {
    solid.push(part(
      box(Math.min(1.12, spec.width * 0.093), 0.22, 0.82)
        .translate(halfWidth * fraction, wallHeight + spec.parapetHeight + 0.13, -spec.depth * 0.26),
      'darkSteel',
    ));
  }

  setFootprint(group, spec.width, spec.depth, wallHeight + spec.parapetHeight);
  addMeshes(group, solid, lights);
  return group;
}

export function buildChurch(options: UrbanBuildingOptions = {}): THREE.Group {
  const facing = options.facing ?? 'south';
  const wall = options.wall ?? 'nightStone';
  const base = CHURCH;
  // Het schip volgt hetzelfde verdiepingsraster als de rest van de stad; toren en spits
  // houden hun verhouding tot het schip, zodat de kerk een landmark blijft in plaats van een
  // los gekozen hoogte.
  const naveHeight = (options.floors ?? base.floors) * CITY.floorHeight;
  const scale = naveHeight / base.naveHeight;
  const spec = {
    ...base,
    width: options.width ?? base.width,
    depth: options.depth ?? base.depth,
    naveHeight,
    towerHeight: base.towerHeight * scale,
    spireHeight: base.spireHeight * scale,
  };
  const front = spec.depth / 2;
  // De toren staat in de voorgevel en moet binnen de voetafdruk van het schip blijven.
  const towerWidth = spec.width * 0.325;
  const towerDepth = spec.depth * 0.185;
  const towerZ = front - spec.depth * 0.096;

  const group = new THREE.Group();
  group.name = 'NA_Building_Church';
  group.rotation.y = facingRotation(facing);

  const solid: Part[] = [
    part(beveledBox(spec.width, spec.naveHeight, spec.depth, 0.045).translate(0, spec.naveHeight / 2, 0), wall),
    part(box(spec.width + 0.14, 0.5, spec.depth + 0.12).translate(0, 0.25, 0), 'nightStone'),
    part(box(towerWidth, spec.towerHeight, towerDepth).translate(0, spec.towerHeight / 2, towerZ), wall),
    part(box(towerWidth + 0.32, 0.22, towerDepth + 0.32).translate(0, spec.towerHeight - 0.2, towerZ), 'nightStone'),
    part(box(towerWidth + 0.7, 0.16, towerDepth + 0.7).translate(0, spec.towerHeight + 0.08, towerZ), 'darkSteel'),
    part(steppedGable(spec.width + 0.2, 2.1, 0.45, 3).translate(0, spec.naveHeight, front + 0.08), wall),
    part(box(1.2, 2.4, 0.16).translate(0, 1.2, front + 0.1), 'charcoal'),
    part(box(1.46, 0.16, 0.18).translate(0, 2.48, front + 0.12), 'nightStone'),
    part(box(0.16, 2.42, 0.18).translate(-0.72, 1.23, front + 0.12), 'nightStone'),
    part(box(0.16, 2.42, 0.18).translate(0.72, 1.23, front + 0.12), 'nightStone'),
    part(box(0.36, spec.naveHeight - 0.6, 0.52).translate(-spec.width / 2 + 0.18, spec.naveHeight / 2, -spec.depth * 0.292), 'nightStone'),
    part(box(0.36, spec.naveHeight - 0.6, 0.52).translate(spec.width / 2 - 0.18, spec.naveHeight / 2, -spec.depth * 0.292), 'nightStone'),
    part(box(0.36, spec.naveHeight - 0.6, 0.52).translate(-spec.width / 2 + 0.18, spec.naveHeight / 2, spec.depth * 0.154), 'nightStone'),
    part(box(0.36, spec.naveHeight - 0.6, 0.52).translate(spec.width / 2 - 0.18, spec.naveHeight / 2, spec.depth * 0.154), 'nightStone'),
    part(box(spec.width + 0.44, 0.18, 0.26).translate(0, spec.naveHeight + 0.08, front + 0.16), 'darkSteel'),
    part(box(spec.width + 0.36, 0.16, 0.22).translate(0, spec.naveHeight + 0.06, -front - 0.12), 'darkSteel'),
    part(box(0.18, spec.spireHeight, 0.18).translate(0, spec.towerHeight + spec.spireHeight / 2, towerZ), 'darkSteel'),
    part(box(1.2, 0.16, 0.18).translate(0, spec.towerHeight + spec.spireHeight - 0.6, towerZ), 'darkSteel'),
    part(box(0.16, 1.2, 0.18).translate(0, spec.towerHeight + spec.spireHeight - 0.6, towerZ), 'darkSteel'),
  ];

  const lights: Part[] = [
    part(box(0.82, 1.3, 0.08).translate(-spec.width * 0.275, spec.naveHeight * 0.611, front + 0.09), 'windowWarm'),
    part(box(0.82, 1.3, 0.08).translate(spec.width * 0.275, spec.naveHeight * 0.611, front + 0.09), 'windowWarm'),
    part(box(0.72, 1.1, 0.08).translate(0, spec.towerHeight * 0.719, front - 0.02), 'windowWarm'),
  ];

  // De toren is smaller dan het schip en staat erbovenop; de romp die blokkeert is het schip.
  setFootprint(group, spec.width, spec.depth, spec.naveHeight);
  addMeshes(group, solid, lights);
  return group;
}

/**
 * De romp die de speler tegenhoudt, uitgedrukt in exact de variabelen waarmee hij gebouwd is.
 * `world/footprints.ts` leest dit als enige bron van waarheid voor collision en validatie.
 */
function setFootprint(group: THREE.Group, width: number, depth: number, height: number): void {
  group.userData.naFootprint = { width, depth, height };
}

function addMeshes(group: THREE.Group, solid: Part[], lights: Part[]): void {
  const shell = new THREE.Mesh(mergeParts(solid), vertexColored());
  shell.name = 'shell';
  shell.castShadow = true;
  shell.receiveShadow = true;
  shell.userData.naOccluder = true;
  group.add(shell);

  if (lights.length > 0) {
    const glow = new THREE.Mesh(mergeParts(lights), emissive('windowWarm', 0.9));
    glow.name = 'windows';
    group.add(glow);
  }
}

function facingRotation(facing: Facing): number {
  switch (facing) {
    case 'south':
      return 0;
    case 'north':
      return Math.PI;
    case 'east':
      return -Math.PI / 2;
    case 'west':
      return Math.PI / 2;
  }
}
